import { keepPreviousData, Updater, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AppID, Filter } from '@playq/octopus-common';
import {
  InventoryItemBrief,
  InventoryItemPromotions,
  ItemsSort,
  QueryInventoryItemsResponse,
} from '@playq/octopus2-economy';
import { ItemID } from '@playq/services-bookkeeper';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';
import { PermModule } from '@playq/octopus2-auth';

import {
  IUseQueryOptionsExtended,
  useEitherQuery,
  useMutateQueryDataAfterEntityDeleting,
  useQueryPrefetch,
} from '/api/service-hooks';
import { iteratorMaxLimit } from '/constants';
import { appToolkit } from '/store';
import { useModulePermissions } from '/hooks';

import { IEntityPromotion } from '../promotions';

import { appsInventoryQueryKeys, appsInventoryService } from './constants';

const emptyArray: [] = [];

const defaultQuery = {
  iterator: new OffsetLimit({ limit: iteratorMaxLimit, offset: 0 }),
  sortBy: [],
  filterBy: {},
};

export const useAppsInventoryQuery = (
  iterator: OffsetLimit = defaultQuery.iterator,
  sortBy: ItemsSort[] = defaultQuery.sortBy,
  filterBy: {
    [key: string]: Filter;
  } = defaultQuery.filterBy,
  options?: IUseQueryOptionsExtended<QueryInventoryItemsResponse | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID);

  const queryClient = useQueryClient();

  const { canRead } = useModulePermissions(PermModule.EconomyInventory);

  const keys = useMemo(
    () => appsInventoryQueryKeys.concat(appID, iterator, sortBy, filterBy),
    [appID, iterator, sortBy, filterBy]
  );

  const isValid = appID !== undefined && canRead;
  const enabled = isValid && options?.enabled !== false;
  const enablePrefetch = isValid && options?.enablePrefetch !== false;

  const { mutate, refetch, ...res } = useEitherQuery(
    keys,
    () => appsInventoryService.queryItems(appID as AppID, iterator, sortBy, filterBy),
    {
      placeholderData: keepPreviousData,
      ...options,
      enabled,
    }
  );
  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);

  const { nextKeys } = useQueryPrefetch({
    keys,
    enabled: enablePrefetch,
    args: [appID as AppID, iterator, sortBy, filterBy],
    total,
    serviceMethod: appsInventoryService.queryItems.bind(appsInventoryService),
  });

  const mutateUpdatePromotions = useCallback(
    (promotions: Updater<IEntityPromotion<ItemID>[], IEntityPromotion<ItemID>[]>) => {
      mutate((prevResponse) => {
        if (!prevResponse) {
          return;
        }
        const response = new QueryInventoryItemsResponse(prevResponse.serialize());
        response.items = prevResponse.items;
        response.iterator = prevResponse.iterator;
        response.features = prevResponse.features;
        response.total = prevResponse.total;
        const nextPromtions = typeof promotions === 'function' ? promotions(response.promotions) : promotions;
        response.promotions = nextPromtions.map(
          (promotion) =>
            new InventoryItemPromotions({ id: promotion.id.serialize(), promotions: promotion.promotions.serialize() })
        );
        return response;
      });
    },
    [mutate]
  );
  const mutateDeletedItem = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    nextPageQueryKey: nextKeys,
    total,
    iterator,
    mutate,
    refetch,
    getID: (item?: InventoryItemBrief) => item?.id,
    getEntities: (entity: QueryInventoryItemsResponse) => entity.items,
    setEntities: (entity: QueryInventoryItemsResponse, items: InventoryItemBrief[]) => {
      entity.items = items;
    },
    createNewResponse: (prevResponse: QueryInventoryItemsResponse) =>
      new QueryInventoryItemsResponse(prevResponse.serialize()),
  });

  const removeQueries = useCallback(() => {
    queryClient.removeQueries({ queryKey: appsInventoryQueryKeys });
  }, [queryClient]);

  return {
    ...res,
    items: res?.data?.items || emptyArray,
    features: res?.data?.features || [],
    promotions: res?.data?.promotions || ([] as IEntityPromotion<ItemID>[]),
    total,
    tags: keys,
    mutatePromotion: mutateUpdatePromotions,
    mutateDeletedItem,
    removeQueries,
    refetch,
  };
};
